import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Users, Activity, ClipboardCheck, Database, FileText, ArrowUpRight, TrendingUp, AlertTriangle, PieChart, Ban } from "lucide-react";

const tiles = [
  { key: "total_customers", label: "Total customers", icon: Users, to: "/admin/customers" },
  { key: "active_subscriptions", label: "Active subscriptions", icon: Activity, to: "/admin/customers" },
  { key: "pending_approvals", label: "Pending approvals", icon: ClipboardCheck, to: "/admin/approvals", accent: true },
  { key: "total_gst_records", label: "GST records", icon: Database, to: "/admin/data" },
  { key: "total_mca_records", label: "MCA records", icon: Database, to: "/admin/data" },
  { key: "gst_files", label: "GST files uploaded", icon: FileText, to: "/admin/uploads" },
  { key: "mca_files", label: "MCA files uploaded", icon: FileText, to: "/admin/uploads" },
];

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);

  const fetchAll = () => {
    api.get("/admin/dashboard").then((r) => setStats(r.data));
    api.get("/admin/analytics").then((r) => setAnalytics(r.data));
  };
  useEffect(() => {
    fetchAll();
    const t = setInterval(fetchAll, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="animate-fade-up">
      <div className="mb-8">
        <h1 className="font-heading font-bold text-3xl sm:text-4xl">Admin overview</h1>
        <p className="mt-1.5 text-sm text-muted-foreground">Realtime snapshot of the platform</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {tiles.map((t) => (
          <Link key={t.key} to={t.to} className="group" data-testid={`stat-${t.key}`}>
            <Card className={`p-5 h-full hover:-translate-y-0.5 transition-transform ${t.accent ? "border-primary/50" : ""}`}>
              <div className="flex items-center justify-between">
                <div className={`h-9 w-9 rounded-md grid place-items-center ${t.accent ? "bg-primary text-primary-foreground" : "bg-primary/10 text-primary"}`}>
                  <t.icon className="h-4 w-4" strokeWidth={1.75} />
                </div>
                <ArrowUpRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              <div className="mt-4 text-xs uppercase tracking-widest text-muted-foreground">{t.label}</div>
              <div className="mt-1 font-heading font-bold text-3xl num">
                {stats ? (stats[t.key] ?? 0).toLocaleString() : "—"}
              </div>
            </Card>
          </Link>
        ))}
      </div>

      {/* Analytics */}
      <div className="mt-10">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="h-4 w-4 text-primary" />
          <h2 className="font-heading font-bold text-2xl">Usage analytics</h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="p-5">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Active clients</div>
            <div className="mt-1 font-heading font-bold text-3xl num text-emerald-600 dark:text-emerald-400" data-testid="analytics-active">
              {analytics ? analytics.active_clients : "—"}
            </div>
          </Card>
          <Card className="p-5">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Expired clients</div>
            <div className="mt-1 font-heading font-bold text-3xl num text-destructive" data-testid="analytics-expired">
              {analytics ? analytics.expired_clients : "—"}
            </div>
          </Card>
          <Card className="p-5">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Files uploaded today</div>
            <div className="mt-1 font-heading font-bold text-3xl num" data-testid="analytics-today">
              {analytics ? analytics.files_today : "—"}
            </div>
          </Card>
          <Card className="p-5">
            <div className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-1"><Ban className="h-3 w-3" /> Suspended</div>
            <div className="mt-1 font-heading font-bold text-3xl num text-yellow-600 dark:text-yellow-400" data-testid="analytics-suspended">
              {analytics ? analytics.suspended_clients : "—"}
            </div>
          </Card>
        </div>

        <div className="mt-4 grid md:grid-cols-2 gap-4">
          <Card className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <PieChart className="h-4 w-4 text-primary" />
              <div className="font-heading font-semibold">Subscriptions by type</div>
            </div>
            <div className="space-y-3">
              {analytics && ["gst", "mca", "both"].map((k) => {
                const v = analytics.by_subscription_type?.[k] ?? 0;
                const total = Object.values(analytics.by_subscription_type || {}).reduce((a, b) => a + b, 0) || 1;
                const pct = Math.round((v / total) * 100);
                return (
                  <div key={k} data-testid={`by-type-${k}`}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium uppercase tracking-wide">{k}</span>
                      <span className="num text-muted-foreground">{v} · {pct}%</span>
                    </div>
                    <div className="mt-1 h-1.5 rounded-full bg-muted overflow-hidden">
                      <div className={`h-full ${k === "gst" ? "bg-primary" : k === "mca" ? "bg-emerald-500" : "bg-amber-500"}`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>

          <Card className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              <div className="font-heading font-semibold">Expiring this week</div>
            </div>
            {analytics?.expiring_this_week?.length ? (
              <div className="space-y-2 max-h-64 overflow-auto">
                {analytics.expiring_this_week.map((c) => (
                  <div key={c.user_id} className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm" data-testid={`expiring-${c.user_id}`}>
                    <div className="min-w-0">
                      <div className="font-medium truncate">{c.name} <span className="text-muted-foreground font-normal">· {c.company}</span></div>
                      <div className="text-xs num text-muted-foreground truncate">{c.email}</div>
                    </div>
                    <Badge variant={c.days_left <= 3 ? "destructive" : "secondary"} className="ml-2 shrink-0 num">
                      {c.days_left}d
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">No subscriptions expiring in the next 7 days.</div>
            )}
          </Card>
        </div>
      </div>

      <div className="mt-10 grid md:grid-cols-2 gap-4">
        <Card className="p-6">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Quick actions</div>
          <div className="mt-4 flex flex-wrap gap-2">
            <Link to="/admin/uploads"><Button data-testid="quick-upload">Upload new file</Button></Link>
            <Link to="/admin/approvals"><Button variant="outline" data-testid="quick-approvals">Review approvals</Button></Link>
            <Link to="/admin/data"><Button variant="outline" data-testid="quick-export">Export data</Button></Link>
          </div>
        </Card>
        <Card className="p-6">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Platform</div>
          <div className="mt-4 text-sm text-muted-foreground leading-relaxed">
            File uploads auto-detect GST vs MCA from CSV headers. Every download is logged per client with timestamp and IP. Notifications and renewal reminders auto-clean after 24 hours.
          </div>
        </Card>
      </div>
    </div>
  );
}
